import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { trackEvent } from '../utils/analytics';

const Footer = () => {
  const { t, currentLang, setIsContactModalOpen } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const pageParam = searchParams.get('page') || searchParams.get('pages');
  const isHomePage = ['/en', '/gr', '/de', '/es', '/fr', '/', ''].includes(location.pathname.replace(/\/$/, '').toLowerCase()) && !pageParam;

  const getHref = (href) => {
    if (!href) return '#';
    if (href.startsWith('#') && !isHomePage) {
      return `/${currentLang}${href}`;
    }
    return href;
  };

  const handleNavClick = (e, href, onClick) => {
    if (onClick) {
      e.preventDefault();
      onClick(e);
      return;
    }
    if (!href || href === '#') {
      e.preventDefault();
      return;
    }
    if (href.startsWith('http://') || href.startsWith('https://')) {
      return;
    }

    e.preventDefault();

    if (href.startsWith('#')) {
      if (isHomePage) {
        const id = href.replace('#', '');
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
        window.history.pushState(null, '', href);
      } else {
        navigate(`/${currentLang}${href}`);
      }
      return;
    }

    if (href.includes('#')) {
      navigate(href);
      const hash = href.split('#')[1];
      if (hash) {
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
          }
        }, 120);
      }
      return;
    }

    navigate(href);
  };

  const directoryBadges = [
    {
      name: "Tech Summit",
      url: "https://tech-summit.org/exhibitor/bookmein-events/",
      image: "/badges/tech-summit.png",
      alt: "BookMeIn.Events - Featured on Tech Summit"
    },
    {
      name: "ViennaUP",
      url: "https://viennaup.com/",
      image: "/badges/vienna-up.png",
      alt: "BookMeIn.Events - Part of ViennaUP"
    },
    {
      name: "Turbo0",
      url: "https://turbo0.com/item/bookmeinevents",
      image: "/badges/turbo0.png",
      alt: "Listed on Turbo0"
    },
    {
      name: "Startup Fame",
      url: "https://startupfa.me/s/bookmeinevents?utm_source=bookmein.events",
      image: "/badges/startup-fame.webp",
      alt: "BookMeIn.Events - Featured on Startup Fame"
    },
    {
      name: "Fazier",
      url: "https://fazier.com/launches/bookmein.events",
      image: "/badges/fazier.png",
      alt: "Launched on Fazier"
    },
    {
      name: "Findly.tools",
      url: "https://findly.tools/bookmein-events?utm_source=bookmein-events",
      image: "/badges/findly-tools.png",
      alt: "Featured on Findly.tools"
    },
    {
      name: "FoundrList",
      url: "https://www.foundrlist.com/product/bookmeinevents",
      image: "/badges/foundrlist.png",
      alt: "Live on FoundrList"
    },
    {
      name: "SaaSHub",
      url: "https://www.saashub.com/bookmein-events?utm_source=badge&utm_campaign=badge&utm_content=bookmein-events&badge_variant=color&badge_kind=approved",
      image: "/badges/saashub.png",
      alt: "Approved on SaaSHub"
    }
  ];

  return (
    <footer className="bg-slate-950 pt-20 pb-24 md:pb-10 border-t border-slate-900">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-16">
            
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <a 
                  href={`/${currentLang}`}
                  onClick={(e) => handleNavClick(e, `/${currentLang}`)}
                  className="cursor-pointer"
                >
                  <img src="/logo-footer.png" alt="BookMeIn.Events Logo" className="h-8 w-auto object-contain" />
                </a>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed max-w-sm mb-6">
                {t('footer.desc')}
              </p>
              {/* Social Links placeholder */}
              <div className="flex gap-4">
                {['Twitter', 'Linkedin', 'Github'].map((social) => (
                  <a key={social} href="#" className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:border-indigo-500 transition-colors">
                    <SafeIcon name={social} className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Legal Section */}
            <div>
              <h4 className="text-white font-semibold mb-6">{t('footer.legal')}</h4>
              <ul className="space-y-4">
                <li>
                  <a href="https://gettermscdn.com/view/ygh2T/terms-of-service/en-us" target="_blank" rel="noopener noreferrer" className="text-slate-400 text-sm hover:text-white transition-colors">
                    {t('footer.legalLinks.tos')}
                  </a>
                </li>
                <li>
                  <a href="https://gettermscdn.com/view/ygh2T/acceptable-use/en-us" target="_blank" rel="noopener noreferrer" className="text-slate-400 text-sm hover:text-white transition-colors">
                    {t('footer.legalLinks.aup')}
                  </a>
                </li>
                <li>
                  <a 
                    href={`/${currentLang}?page=terms-attendees`} 
                    onClick={(e) => handleNavClick(e, `/${currentLang}?page=terms-attendees`)}
                    className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer"
                  >
                    {t('footer.legalLinks.attendee')}
                  </a>
                </li>
                <li>
                  <a 
                    href={`/${currentLang}?page=terms-organizers`} 
                    onClick={(e) => handleNavClick(e, `/${currentLang}?page=terms-organizers`)}
                    className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer"
                  >
                    {t('footer.legalLinks.organizer')}
                  </a>
                </li>
                <li>
                  <a 
                    href={`/${currentLang}?page=privacy-policy`} 
                    onClick={(e) => handleNavClick(e, `/${currentLang}?page=privacy-policy`)}
                    className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer"
                  >
                    {t('footer.privacy')}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-6">{t('footer.platform')}</h4>
              <ul className="space-y-4">
                <li>
                  <a 
                    href={getHref('#pricing')} 
                    onClick={(e) => handleNavClick(e, getHref('#pricing'))}
                    className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer"
                  >
                    {t('navbar.items.pricing')}
                  </a>
                </li>
                <li>
                  <a 
                    href="https://docs.bookmein.events/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-slate-400 text-sm hover:text-white transition-colors"
                  >
                    API Docs
                  </a>
                </li>
                <li>
                  <a 
                    href="https://status.bookmein.events/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => trackEvent('footer_link_click', { destination: 'status_page' })}
                    className="text-slate-400 text-sm hover:text-white transition-colors"
                  >
                    App Status
                  </a>
                </li>
                <li>
                  <a 
                    href="https://help.bookmein.events/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => trackEvent('footer_link_click', { destination: 'help_center' })}
                    className="text-slate-400 text-sm hover:text-white transition-colors"
                  >
                    {t('footer.helpCenter')}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-6">{t('footer.solutions')}</h4>
              <ul className="space-y-3">
                {[
                  { name: 'Pilot', id: 'pilot' },
                  { name: 'Unity', id: 'unity' },
                  { name: 'Scotty', id: 'scotty' },
                  { name: 'Pollio', id: 'pollio' },
                  { name: 'Ecosystem', id: 'ecosystem' },
                  { name: 'Memories', id: 'memories' },
                  { name: 'Avatars', id: 'avatars' },
                  { name: 'Bifrost', id: 'bifrost' },
                  { name: 'Nima', id: 'nima' }
                ].map((item) => (
                  <li key={item.id}>
                    <a 
                      href={`/${currentLang}?page=solutions&tab=${item.id}`} 
                      onClick={(e) => handleNavClick(e, `/${currentLang}?page=solutions&tab=${item.id}`)}
                      className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-6">{t('footer.company')}</h4>
              <ul className="space-y-4">
                {[
                  { name: 'About Us', href: `/${currentLang}?page=solutions&tab=ecosystem` },
                  { name: 'Brand Assets', href: getHref('#branding') },
                  { name: 'Contact Support', isContact: true },
                  { name: 'Privacy Policy', href: `/${currentLang}?page=privacy-policy` },
                ].map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href || '#'}
                      onClick={(e) => {
                        if (item.isContact) {
                          e.preventDefault();
                          trackEvent('open_contact_form', { source: 'footer' });
                          setIsContactModalOpen(true);
                        } else {
                          handleNavClick(e, item.href);
                        }
                      }}
                      className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

          </div>

          {/* Directory Badges Horizontal Section */}
          <div className="pt-8 pb-10 border-t border-slate-900 my-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex items-center gap-3 shrink-0">
                <span className="w-1.5 h-6 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></span>
                <h4 className="text-white font-semibold text-lg tracking-tight">
                  {t('footer.asFeaturedOn')}
                </h4>
              </div>
              
              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                {directoryBadges.map((badge, idx) => (
                  <a
                    key={idx}
                    href={badge.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('directory_badge_click', { directory: badge.name })}
                    className="bg-white/95 hover:bg-white rounded-xl p-2 px-3.5 flex items-center justify-center border border-white/20 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-indigo-500/15 group h-11 overflow-hidden"
                    title={badge.name}
                  >
                    <img 
                      src={badge.image} 
                      alt={badge.alt} 
                      className={`w-auto object-contain transition-transform duration-300 group-hover:scale-110 ${badge.imgClass || 'max-h-7 max-w-[140px]'}`} 
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              {t('footer.copyright', {year: new Date().getFullYear()})} <a href="https://bookmein.events/en?page=privacy-policy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700">BMI Software S.A.</a>
            </p>
            <div className="flex gap-6 text-sm text-slate-500">
              <a href="https://gettermscdn.com/view/ygh2T/return/en-us" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                {t('footer.refunds')}
              </a>
              <a 
                href={`/${currentLang}?page=privacy-policy`} 
                onClick={(e) => handleNavClick(e, `/${currentLang}?page=privacy-policy`)}
                className="hover:text-white transition-colors cursor-pointer"
              >
                {t('footer.privacy')}
              </a>
              <a href="https://gettermscdn.com/view/ygh2T/cookies/en-us" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                {t('footer.cookies')}
              </a>
            </div>
          </div>
        </div>
      </footer>
  );
};

export default Footer;